/**
 * Answer matching for the vocabulary-set drill (VSM-005).
 *
 * Generous about FORM, strict about the WORD.
 *
 * Accepted: different case, stray whitespace, a leading article, and the
 * `en_GB` variant of the lemma — a learner who types *colour* has produced the
 * word, and telling them otherwise teaches them a falsehood about English.
 *
 * Deliberately NOT fuzzy beyond that: no edit distance, no prefix matching. A
 * near-miss would write `produced`, and `produced` is the counter the whole
 * mastery rule is built on (ADR-034). Accepting *recieve* would record that the
 * learner can produce a word they cannot spell.
 */

const LEADING_ARTICLE = /^(?:a|an|the)\s+/i;

/** Case-folded, trimmed, de-articled, whitespace-collapsed. */
export function normalizeAnswer(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(LEADING_ARTICLE, '')
    .trim();
}

/**
 * @param typed    what the learner wrote
 * @param lemma    the catalog lemma — always the US spelling, it is the join key
 * @param enGB     the British rendering, when it differs
 */
export function isCorrectAnswer(typed: string, lemma: string, enGB?: string): boolean {
  const got = normalizeAnswer(typed);
  if (!got) return false;
  if (got === normalizeAnswer(lemma)) return true;
  return enGB ? got === normalizeAnswer(enGB) : false;
}

/**
 * Scripted-mode completeness floor — "did they attempt the target at all",
 * NOT a quality bar.
 *
 * Chosen, not inherited: the codebase has no completeness threshold to reuse.
 * Its neighbours are all quality bars measuring something else — 85 for phoneme
 * mastery (`soundMap.ts`), 70 for "great job" feedback, 40 for the mic test —
 * and borrowing one of those would quietly turn this into an accent gate.
 *
 * Deliberately low. It exists for one failure mode: Azure's scripted assessment
 * BIASES recognition toward `referenceText`, so a learner who says nothing
 * useful can still come back with a transcription that resembles the target.
 * Completeness measures how much of the reference was actually spoken, so it is
 * the signal that survives that bias.
 */
export const MIN_COMPLETENESS_FOR_PRODUCED = 50;

export interface SpokenAttempt {
  /** Did the learner RECALL the word? The only thing that decides the mark. */
  correct: boolean;
  /**
   * Why, for the UI. `wrong-word` and `not-spoken` are both `struggled`, but a
   * learner deserves to know which one happened — "we didn't hear you" and
   * "that was the wrong word" call for different next actions.
   */
  reason: 'match' | 'wrong-word' | 'not-spoken';
}

/**
 * Judge a spoken attempt at a set word.
 *
 * **Recall and pronunciation are different axes, and only recall decides the
 * mark.** The drill asks "do you know the word for *rojo*"; the app already has
 * a surface whose job is pronunciation quality (the Sound Map, ADR-034's
 * phoneme ladder). Gating `produced` on an accuracy score would mean a learner
 * who knows the word perfectly never gets credit for knowing it, and would put
 * two features in the app measuring the same thing by different rules — the
 * failure this codebase has hit repeatedly.
 *
 * So the accuracy/pronunciation scores are shown as feedback and change
 * nothing. `completenessScore` is the one score consulted, and only to defend
 * against the scripted-mode bias described above.
 */
export function evaluateSpokenAttempt(
  transcription: string,
  lemma: string,
  enGB: string | undefined,
  completenessScore: number | undefined,
): SpokenAttempt {
  if (!isCorrectAnswer(transcription, lemma, enGB)) return { correct: false, reason: 'wrong-word' };
  // Transcription matched — but with a reference set that is weak evidence on
  // its own, so require that some of the reference was actually spoken.
  if (completenessScore !== undefined && completenessScore < MIN_COMPLETENESS_FOR_PRODUCED) {
    return { correct: false, reason: 'not-spoken' };
  }
  return { correct: true, reason: 'match' };
}
